import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, timer } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { CourseCreateDialogComponent } from '../../../components/dialogs/course-create-dialog/course-create-dialog.component';
import { DispatchExerciseDialogComponent } from '../../../components/dialogs/dispatch-exercise-dialog/dispatch-exercise-dialog.component';
import { SubmissionReviewDialogComponent } from '../../../components/dialogs/submission-review-dialog/submission-review-dialog.component';
import { MoodleCoursesDialogComponent } from '../../../components/dialogs/moodle-courses-dialog/moodle-courses-dialog.component';
import { ParticipantDetailDialogComponent } from '../../../components/dialogs/participant-detail-dialog/participant-detail-dialog.component';
import {
  CourseParticipant,
  DozentService,
  MoodleRefreshStatus,
  MoodleRefreshStatusKind,
} from '../../../services/dozent.service';
import { DozentSocketService } from '../../../services/dozent-socket.service';
import { participantPseudonym } from '../../../helpers/participant-pseudonym';

@Component({
  selector: 'app-dozent-participants',
  templateUrl: './participants.component.html',
  styleUrl: './participants.component.scss',
})
export class DozentParticipantsComponent implements OnInit, OnDestroy {
  courses: any[] = [];
  participants: CourseParticipant[] = [];

  selectedCourse: any = null;
  isLoading = true;
  isLoadingParticipants = false;
  isSyncing = false;
  isSyncingSelectedCourse = false;
  syncProgress: MoodleRefreshStatus | null = null;
  searchText = '';
  courseSortDirection: 'asc' | 'desc' = 'asc';
  coursePage = 0;
  coursePageSize = 25;
  coursePageSizeOptions = [25, 50, 100];
  courseTotal = 0;
  participantPage = 0;
  participantPageSize = 25;
  participantPageSizeOptions = [25, 50, 100];
  participantTotal = 0;

  // Add participant form
  showAddForm = false;
  isAdding = false;
  newParticipant = { first_name: '', last_name: '', email: '' };

  // Edit participant
  editingParticipant: CourseParticipant | null = null;
  editModel = { first_name: '', last_name: '', email: '' };

  // Detail view
  selectedParticipant: CourseParticipant | null = null;

  // Online status
  onlineStatuses: Record<string, boolean> = {};
  private presenceSub?: Subscription;
  private syncStatusPollSub?: Subscription;

  // Course assignments (Übungen tab)
  courseAssignments: any[] = [];
  isLoadingAssignments = false;
  assignmentPage = 0;
  assignmentPageSize = 25;
  assignmentPageSizeOptions = [25, 50, 100];

  constructor(
    private dozentService: DozentService,
    private socketService: DozentSocketService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private t: TranslateService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.loadCourses();

    // Listen for real-time presence updates
    this.socketService.connect();
    this.presenceSub = this.socketService.presenceUpdate$.subscribe((update) => {
      if (update?.user_id) {
        this.onlineStatuses[update.user_id] = update.is_online;
      }
    });

    // Deep-link handler for `submission_review` notifications coming
    // from the header bell. The URL carries the assignment + the
    // exact submission to preselect; we open the review dialog
    // directly so the dozent lands on the submission they were
    // notified about. Subscribed without `take(1)` so re-navigation
    // from the bell while already on this page (the most common
    // case for a live notification) still triggers — `take(1)` only
    // fired once per component lifetime, so subsequent clicks on
    // notifications dead-ended.
    this.route.queryParams.subscribe((params) => {
      const assignmentId = params['assignmentId'];
      const submissionId = params['submissionId'];
      if (assignmentId && assignmentId !== this.lastDeepLinkedAssignmentId) {
        this.lastDeepLinkedAssignmentId = assignmentId;
        this.openSubmissionReviewById(assignmentId, submissionId);
      }
    });
  }

  /**
   * Tracks the last assignment-id we already opened a dialog for so
   * a stale `queryParams` emission (e.g. on tab focus) doesn't pop
   * the dialog twice.
   */
  private lastDeepLinkedAssignmentId: string | null = null;

  ngOnDestroy(): void {
    this.presenceSub?.unsubscribe();
    this.syncStatusPollSub?.unsubscribe();
  }

  loadCourses() {
    this.isLoading = true;
    this.dozentService.listCoursesPage({
      limit: this.coursePageSize,
      offset: this.coursePage * this.coursePageSize,
      search: this.searchText,
    }).subscribe({
      next: (resp) => {
        this.courses = resp.data ?? [];
        this.courseTotal = resp.total ?? resp.pagination?.total ?? this.courses.length;
        this.clampCoursePage();
        this.isLoading = false;
      },
      error: () => (this.isLoading = false),
    });
  }

  onSelectCourse(course: any) {
    this.selectedCourse = course;
    this.participantPage = 0;
    this.assignmentPage = 0;
    this.loadParticipants(course.id);
    this.loadCourseAssignments(course.id);
  }

  loadCourseAssignments(courseId: string) {
    this.isLoadingAssignments = true;
    this.dozentService.listCourseAssignments(courseId).subscribe({
      next: (rows: any) => {
        this.courseAssignments = rows ?? [];
        this.clampAssignmentPage();
        this.isLoadingAssignments = false;
      },
      error: () => (this.isLoadingAssignments = false),
    });
  }

  openDispatchExerciseDialog() {
    if (!this.selectedCourse) return;
    this.dialogService.open(DispatchExerciseDialogComponent, {
      context: {
        courseId: this.selectedCourse.id,
        participants: this.participants,
        onDispatched: () => {
          if (this.selectedCourse) {
            this.loadCourseAssignments(this.selectedCourse.id);
          }
        },
      },
    });
  }

  openSubmissionReview(assignment: any) {
    this.dialogService.open(SubmissionReviewDialogComponent, {
      context: {
        assignmentId: assignment.id,
      },
    });
  }

  /** Open the review dialog from a notification deep-link, with the
   *  exact submission preselected (instead of the first one). */
  openSubmissionReviewById(assignmentId: string, submissionId?: string) {
    this.dialogService.open(SubmissionReviewDialogComponent, {
      context: {
        assignmentId,
        preselectSubmissionId: submissionId,
      },
    });
  }

  onDeleteAssignment(assignment: any) {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        onConfirm: () => {
          this.dozentService.deleteAssignment(assignment.id).subscribe({
            next: () => {
              if (this.selectedCourse) {
                this.loadCourseAssignments(this.selectedCourse.id);
              }
              ref.close();
            },
            error: () => {
              this.toastrService.danger(this.t.instant('toasts.error'), '');
            },
          });
        },
      },
    });
  }

  loadParticipants(courseId: string) {
    this.isLoadingParticipants = true;
    this.dozentService.listParticipantsPage(courseId, {
      limit: this.participantPageSize,
      offset: this.participantPage * this.participantPageSize,
    }).subscribe({
      next: (resp) => {
        this.participants = resp.data ?? [];
        this.participantTotal = resp.total ?? resp.pagination?.total ?? this.participants.length;
        this.clampParticipantPage();
        this.isLoadingParticipants = false;
        this.fetchOnlineStatuses();
      },
      error: () => (this.isLoadingParticipants = false),
    });
  }

  fetchOnlineStatuses() {
    const ids = this.participants
      .map((p) => p.learner_user_id)
      .filter((id): id is string => !!id);
    if (ids.length === 0) return;
    this.dozentService.getOnlineStatuses(ids).subscribe({
      next: (statuses: any) => {
        this.onlineStatuses = { ...this.onlineStatuses, ...statuses };
      },
    });
  }

  isOnline(p: CourseParticipant): boolean {
    return !!p.learner_user_id && !!this.onlineStatuses[p.learner_user_id];
  }

  // ── Course creation dialog ──

  openCreateCourseDialog() {
    this.dialogService.open(CourseCreateDialogComponent, {
      context: {
        onConfirm: (result: any) => {
          const body: any = { course_name: result.course_name, source_type: 'manual' };
          if (result.description) body.description = result.description;
          this.dozentService
            .createCourse(body)
            .subscribe({
              next: () => {
                this.toastrService.success(
                  this.t.instant('pages.dozent.participants.courseCreated'),
                  ''
                );
                this.loadCourses();
              },
              error: () => {
                this.toastrService.danger(this.t.instant('toasts.error'), '');
              },
            });
        },
      },
    });
  }

  // ── Moodle import dialog ──

  openMoodleImportDialog() {
    this.dialogService.open(MoodleCoursesDialogComponent, {
      context: {
        // Dozent flow: only metadata + participants, no content sync.
        metaOnly: true,
        onImportComplete: () => {
          // The meta import endpoint is synchronous, so by the time the
          // dialog closes the course rows + participants already exist.
          this.loadCourses();
          if (this.selectedCourse) {
            this.loadParticipants(this.selectedCourse.id);
          }
        },
      },
    });
  }

  onRefreshMoodleSync() {
    if (this.isSyncing) return;
    const requestedAt = new Date().toISOString();
    this.isSyncing = true;
    this.syncProgress = this.initialSyncProgress();
    this.dozentService.refreshMoodleCourseAndParticipantSync().subscribe({
      next: (response: any) => {
        this.toastrService.success(
          this.t.instant('pages.dozent.participants.syncQueued'),
          ''
        );
        this.loadCourses();
        this.waitForMoodleEnrollmentRefresh(this.refreshStartedAt(response, requestedAt));
      },
      error: (err) => {
        this.isSyncing = false;
        this.syncProgress = null;
        const detail = err?.error?.detail || this.t.instant('pages.dozent.participants.syncError');
        this.toastrService.danger(detail, '');
      },
    });
  }

  get selectedCourseIsMoodle(): boolean {
    return !!(
      this.selectedCourse?.moodle_course_id ||
      this.selectedCourse?.original_course_id
    );
  }

  onRefreshSelectedCourseParticipants() {
    if (
      !this.selectedCourse ||
      !this.selectedCourseIsMoodle ||
      this.isSyncingSelectedCourse
    ) {
      return;
    }
    const courseId = this.selectedCourse.id;
    this.isSyncingSelectedCourse = true;
    this.dozentService.refreshMoodleParticipantsForCourse(courseId).subscribe({
      next: () => {
        this.toastrService.success(
          this.t.instant('pages.dozent.participants.syncCourseSuccess'),
          ''
        );
        this.isSyncingSelectedCourse = false;
        this.loadParticipants(courseId);
        this.loadCourses();
      },
      error: (err) => {
        this.isSyncingSelectedCourse = false;
        const detail = err?.error?.detail || this.t.instant('pages.dozent.participants.syncError');
        this.toastrService.danger(detail, '');
      },
    });
  }

  get syncProgressPercent(): number {
    const progress = this.syncActiveProgress;
    if (progress && progress.total > 0) {
      return Math.round(((progress.done + progress.failed) / progress.total) * 100);
    }
    return this.syncProgress?.percent ?? 0;
  }

  get syncProgressStatus(): 'primary' | 'success' | 'warning' | 'danger' {
    const state = this.syncProgress?.state;
    if (state === 'completed') return 'success';
    if (state === 'completed_with_errors' || state === 'running_with_errors') return 'warning';
    return 'primary';
  }

  get syncProgressIcon(): string {
    if (!this.syncProgress) return 'sync-outline';
    if (this.syncProgress.state === 'completed') return 'checkmark-circle-2-outline';
    if (this.syncProgress.state === 'completed_with_errors' || this.syncProgress.state === 'running_with_errors') {
      return 'alert-triangle-outline';
    }
    return 'sync-outline';
  }

  get syncProgressRunning(): boolean {
    return this.isSyncing
      && (this.syncProgress?.state === 'running' || this.syncProgress?.state === 'running_with_errors');
  }

  get syncProgressLabel(): string {
    if (!this.syncProgress) return '';
    const courseMeta = this.syncCourseMetaProgress;
    const enrollments = this.syncEnrollmentProgress;
    const progress = this.syncActiveProgress;
    const total = progress?.total ?? this.syncProgress.total;
    const done = progress?.done ?? this.syncProgress.done;
    const failed = progress?.failed ?? this.syncProgress.failed;
    if (total === 0 && this.isSyncing) {
      return this.t.instant('pages.dozent.participants.syncProgress.preparing');
    }
    if (courseMeta && courseMeta.done + courseMeta.failed < courseMeta.total) {
      const key = courseMeta.failed > 0
        ? 'pages.dozent.participants.syncProgress.checkingCoursesWithErrors'
        : 'pages.dozent.participants.syncProgress.checkingCourses';
      return this.t.instant(key, {
        done: courseMeta.done,
        failed: courseMeta.failed,
        total: courseMeta.total,
        percent: this.progressPercentFor(courseMeta),
      });
    }
    if (this.syncProgress.state === 'completed') {
      const key = enrollments && enrollments.total > 0
        ? 'pages.dozent.participants.syncProgress.completed'
        : 'pages.dozent.participants.syncProgress.completedCourses';
      return this.t.instant(key, { total, percent: this.progressPercentFor(progress) });
    }
    if (this.syncProgress.state === 'completed_with_errors') {
      return this.t.instant('pages.dozent.participants.syncProgress.completedWithErrors', {
        done,
        failed,
        total,
        percent: this.progressPercentFor(progress),
      });
    }
    if (this.syncProgress.state === 'running_with_errors') {
      return this.t.instant('pages.dozent.participants.syncProgress.runningWithErrors', {
        done,
        failed,
        total,
        percent: this.progressPercentFor(progress),
      });
    }
    return this.t.instant('pages.dozent.participants.syncProgress.running', {
      done,
      total,
      percent: this.progressPercentFor(progress),
    });
  }

  private progressPercentFor(progress?: MoodleRefreshStatusKind | null): number {
    if (progress && progress.total > 0) {
      return Math.round(((progress.done + progress.failed) / progress.total) * 100);
    }
    return this.syncProgress?.percent ?? 0;
  }

  private get syncCourseMetaProgress(): MoodleRefreshStatusKind | null {
    return this.syncProgress?.by_kind?.['course_meta'] ?? null;
  }

  private get syncEnrollmentProgress(): MoodleRefreshStatusKind | null {
    return this.syncProgress?.by_kind?.['course_enrollments'] ?? null;
  }

  private get syncActiveProgress(): MoodleRefreshStatusKind | null {
    const courseMeta = this.syncCourseMetaProgress;
    if (courseMeta && courseMeta.done + courseMeta.failed < courseMeta.total) {
      return courseMeta;
    }
    return this.syncEnrollmentProgress ?? courseMeta;
  }

  private initialSyncProgress(): MoodleRefreshStatus {
    return {
      state: 'running',
      total: 0,
      done: 0,
      failed: 0,
      pending: 0,
      running: 0,
      queued: 0,
      percent: 0,
    };
  }

  private refreshStartedAt(response: any, fallback: string): string {
    const candidates = Array.isArray(response?.queued)
      ? response.queued.map((item: any) => item?.next_due_at)
      : [response?.next_due_at];
    const dates = candidates
      .concat(fallback)
      .filter((value: any): value is string => typeof value === 'string' && value.length > 0)
      .map((value: string) => new Date(value).getTime())
      .filter((value: number) => Number.isFinite(value));
    if (dates.length === 0) return fallback;
    return new Date(Math.min(...dates)).toISOString();
  }

  private waitForMoodleEnrollmentRefresh(since: string | null) {
    this.syncStatusPollSub?.unsubscribe();
    let polls = 0;
    const maxPolls = 90;
    this.syncStatusPollSub = timer(1000, 2000)
      .pipe(
        switchMap(() => this.dozentService.getMoodleRefreshStatus(since)),
        takeWhile((status) => {
          polls += 1;
          return !this.isEnrollmentRefreshFinished(status) && polls < maxPolls;
        }, true),
      )
      .subscribe({
        next: (status) => {
          this.syncProgress = status;
          if (this.isEnrollmentRefreshFinished(status) || polls >= maxPolls) {
            this.finishMoodleEnrollmentRefresh(status);
          }
        },
        error: () => {
          this.finishMoodleEnrollmentRefresh(null);
        },
      });
  }

  private isEnrollmentRefreshFinished(status: MoodleRefreshStatus): boolean {
    const enrollments = status.by_kind?.['course_enrollments'];
    if (!enrollments || enrollments.total === 0) {
      return status.state === 'idle'
        || status.state === 'completed'
        || status.state === 'completed_with_errors';
    }
    return enrollments.done + enrollments.failed >= enrollments.total;
  }

  private finishMoodleEnrollmentRefresh(status: MoodleRefreshStatus | null) {
    this.syncStatusPollSub?.unsubscribe();
    this.syncStatusPollSub = undefined;
    this.isSyncing = false;
    this.loadCourses();
    if (this.selectedCourse) {
      this.loadParticipants(this.selectedCourse.id);
    }
    if (!status) {
      this.syncProgress = null;
      this.toastrService.danger(
        this.t.instant('pages.dozent.participants.syncError'),
        '',
      );
      return;
    }
    this.syncProgress = status;
    const failed = status?.by_kind?.['course_enrollments']?.failed ?? 0;
    if (failed > 0) {
      this.toastrService.danger(
        this.t.instant('pages.dozent.participants.syncError'),
        '',
      );
    } else if (status && this.isEnrollmentRefreshFinished(status)) {
      this.toastrService.success(
        this.t.instant('pages.dozent.participants.syncSuccess'),
        '',
      );
    }
  }

  onDeleteCourse(course: any, event: Event) {
    event.stopPropagation();
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        onConfirm: () => {
          this.dozentService.deleteCourse(course.id).subscribe({
            next: () => {
              if (this.selectedCourse?.id === course.id) {
                this.selectedCourse = null;
                this.participants = [];
              }
              this.loadCourses();
              ref.close();
            },
            error: () => {
              this.toastrService.danger(this.t.instant('toasts.error'), '');
            },
          });
        },
      },
    });
  }

  // ── Participants ──

  onImportFromMoodle() {
    if (!this.selectedCourse) return;
    const courseId = this.selectedCourse.id;

    // If course is already linked to Moodle, just sync directly
    if (this.selectedCourse.original_course_id || this.selectedCourse.moodle_course_id) {
      this.onSyncMoodle(courseId);
      return;
    }

    // Otherwise: pick a Moodle course to import participants from
    this.dialogService.open(MoodleCoursesDialogComponent, {
      context: {
        pickMode: true,
        onPick: (mc: any) => {
          this.isSyncing = true;
          this.dozentService
            .importMoodleParticipants(courseId, mc.id, true, mc.registration_id)
            .subscribe({
              next: (result: any) => {
                this.isSyncing = false;
                const matched = result?.participants?.matched_known_users ?? 0;
                const imported = result?.participants?.imported ?? 0;
                this.toastrService.success(
                  `${imported} ${this.t.instant('pages.dozent.participants.imported')} · ${matched} ${this.t.instant('pages.dozent.participants.knownLearners')}`,
                  ''
                );
                this.loadParticipants(courseId);
              },
              error: () => {
                this.isSyncing = false;
                this.toastrService.danger(
                  this.t.instant('pages.dozent.participants.syncError'),
                  ''
                );
              },
            });
        },
      },
    });
  }

  onSyncMoodle(courseId: string) {
    this.isSyncing = true;
    this.dozentService.syncMoodleParticipants(courseId).subscribe({
      next: () => {
        this.toastrService.success(
          this.t.instant('pages.dozent.participants.syncSuccess'),
          ''
        );
        this.isSyncing = false;
        if (this.selectedCourse?.id === courseId) {
          this.loadParticipants(courseId);
        }
      },
      error: () => {
        this.toastrService.danger(
          this.t.instant('pages.dozent.participants.syncError'),
          ''
        );
        this.isSyncing = false;
      },
    });
  }

  onAddParticipant() {
    if (!this.selectedCourse || !this.newParticipant.email || this.isAdding) return;
    this.isAdding = true;
    this.dozentService
      .addParticipant(this.selectedCourse.id, this.newParticipant)
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.t.instant('pages.dozent.participants.added'),
            ''
          );
          this.showAddForm = false;
          this.isAdding = false;
          this.newParticipant = { first_name: '', last_name: '', email: '' };
          this.loadParticipants(this.selectedCourse.id);
        },
        error: (err) => {
          this.isAdding = false;
          if (err.status === 409) {
            this.toastrService.warning(
              this.t.instant('pages.dozent.participants.duplicateEmail'),
              ''
            );
          } else {
            this.toastrService.danger(this.t.instant('toasts.error'), '');
          }
        },
      });
  }

  /**
   * Opens the participant-detail view as a modal. Called from the
   * name click, the eye (view) icon, and the pencil (edit) icon in
   * the Teilnehmende table — all three lead to the same deepest
   * learner view (general / profile / exercises / communication tabs).
   * Replaces the previous inline-replacement behaviour where the
   * right-hand tabset was swapped for the detail panel.
   */
  openParticipantDetail(p: CourseParticipant) {
    this.dialogService.open(ParticipantDetailDialogComponent, {
      context: {
        participant: p,
        courses: this.courses,
        currentCourseId: this.selectedCourse?.id ?? null,
      },
      hasBackdrop: true,
      closeOnBackdropClick: true,
      closeOnEsc: true,
    });
  }

  onEditParticipant(p: CourseParticipant) {
    this.editingParticipant = p;
    this.editModel = {
      first_name: p.first_name || '',
      last_name: p.last_name || '',
      email: p.email || '',
    };
  }

  onCancelEdit() {
    this.editingParticipant = null;
  }

  onSaveEdit() {
    if (!this.editingParticipant || !this.selectedCourse) return;
    this.dozentService
      .updateParticipant(this.selectedCourse.id, this.editingParticipant.id, this.editModel)
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.t.instant('pages.dozent.participants.updated'),
            ''
          );
          this.editingParticipant = null;
          this.loadParticipants(this.selectedCourse.id);
        },
        error: (err) => {
          if (err.status === 409) {
            this.toastrService.warning(
              this.t.instant('pages.dozent.participants.duplicateEmail'),
              ''
            );
          } else {
            this.toastrService.danger(this.t.instant('toasts.error'), '');
          }
        },
      });
  }

  onRemoveParticipant(participant: CourseParticipant) {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        onConfirm: () => {
          this.dozentService
            .removeParticipant(this.selectedCourse.id, participant.id)
            .subscribe({
              next: () => {
                this.loadParticipants(this.selectedCourse.id);
                ref.close();
              },
            });
        },
      },
    });
  }

  onImportCsv(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.selectedCourse) return;
    this.dozentService
      .importParticipantsCsv(this.selectedCourse.id, input.files[0])
      .subscribe({
        next: (result: any) => {
          this.toastrService.success(
            `${result?.imported ?? ''} ${this.t.instant('pages.dozent.participants.imported')}`,
            ''
          );
          this.loadParticipants(this.selectedCourse.id);
        },
        error: () => {
          this.toastrService.danger(this.t.instant('toasts.error'), '');
        },
      });
    input.value = '';
  }

  get filteredCourses(): any[] {
    return [...this.courses].sort((a: any, b: any) => {
      const cmp = this.getCourseName(a).localeCompare(
        this.getCourseName(b),
        undefined,
        { sensitivity: 'base', numeric: true }
      );
      return this.courseSortDirection === 'asc' ? cmp : -cmp;
    });
  }

  get pagedCourses(): any[] {
    return this.filteredCourses;
  }

  onCourseSearchChange(): void {
    this.coursePage = 0;
    this.participantPage = 0;
    this.loadCourses();
    if (this.selectedCourse) {
      this.loadParticipants(this.selectedCourse.id);
    }
  }

  toggleCourseSort(): void {
    this.courseSortDirection = this.courseSortDirection === 'asc' ? 'desc' : 'asc';
    this.coursePage = 0;
    this.loadCourses();
  }

  onCoursePageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.courseTotal / this.coursePageSize) - 1, 0);
    this.coursePage = Math.max(0, Math.min(page, maxPage));
    this.loadCourses();
  }

  onCoursePageSizeChange(size: number): void {
    this.coursePageSize = Math.min(Math.max(size, 1), 100);
    this.coursePage = 0;
    this.loadCourses();
  }

  private clampCoursePage(): void {
    const maxPage = Math.max(Math.ceil(this.courseTotal / this.coursePageSize) - 1, 0);
    this.coursePage = Math.max(0, Math.min(this.coursePage, maxPage));
  }

  private getCourseName(course: any): string {
    return course?.name || course?.course_name || '';
  }

  get filteredParticipants(): CourseParticipant[] {
    return this.participants;
  }

  get pagedParticipants(): CourseParticipant[] {
    return this.filteredParticipants;
  }

  onParticipantPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.participantTotal / this.participantPageSize) - 1, 0);
    this.participantPage = Math.max(0, Math.min(page, maxPage));
    if (this.selectedCourse) this.loadParticipants(this.selectedCourse.id);
  }

  onParticipantPageSizeChange(size: number): void {
    this.participantPageSize = Math.min(Math.max(size, 1), 100);
    this.participantPage = 0;
    if (this.selectedCourse) this.loadParticipants(this.selectedCourse.id);
  }

  private clampParticipantPage(): void {
    const maxPage = Math.max(Math.ceil(this.participantTotal / this.participantPageSize) - 1, 0);
    this.participantPage = Math.max(0, Math.min(this.participantPage, maxPage));
  }

  get pagedCourseAssignments(): any[] {
    const maxPage = Math.max(Math.ceil(this.courseAssignments.length / this.assignmentPageSize) - 1, 0);
    const page = Math.min(this.assignmentPage, maxPage);
    const start = page * this.assignmentPageSize;
    return this.courseAssignments.slice(start, start + this.assignmentPageSize);
  }

  onAssignmentPageChange(page: number): void {
    const maxPage = Math.max(Math.ceil(this.courseAssignments.length / this.assignmentPageSize) - 1, 0);
    this.assignmentPage = Math.max(0, Math.min(page, maxPage));
  }

  onAssignmentPageSizeChange(size: number): void {
    this.assignmentPageSize = Math.min(Math.max(size, 1), 100);
    this.assignmentPage = 0;
  }

  private clampAssignmentPage(): void {
    const maxPage = Math.max(Math.ceil(this.courseAssignments.length / this.assignmentPageSize) - 1, 0);
    this.assignmentPage = Math.max(0, Math.min(this.assignmentPage, maxPage));
  }

  get hasPseudonymizedParticipants(): boolean {
    return this.participants.some((p) => !`${p.first_name ?? ''} ${p.last_name ?? ''}`.trim());
  }

  getParticipantName(p: CourseParticipant): string {
    return participantPseudonym(p);
  }

  getSourceBadge(source: string): string {
    switch (source) {
      case 'moodle':
        return 'Moodle';
      case 'csv_import':
        return 'CSV';
      default:
        return this.t.instant('pages.dozent.participants.manual');
    }
  }
}
